import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate, formatDateTime, formatNumber, formatUnit } from "@/lib/format";
import type { components } from "@dolfin/api-admin";

type DeliveryNote = components["schemas"]["DeliveryNoteResponse"];

// The delivery-note half of the match drill-through. See PurchaseOrderPage for why this reads the admin
// by-id endpoint rather than the tenant one.
export function DeliveryNotePage() {
  const { deliveryNoteId } = useParams<{ deliveryNoteId: string }>();
  const [dn, setDn] = useState<DeliveryNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deliveryNoteId) return;
    let cancelled = false;
    setLoading(true);
    api
      .GET("/admin/delivery-notes/{id}", { params: { path: { id: deliveryNoteId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("Delivery note not found.");
          return;
        }
        setDn(data);
      })
      .catch(() => !cancelled && setError("Delivery note could not be loaded."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [deliveryNoteId]);

  if (loading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading delivery note…
      </div>
    );
  }

  if (error || !dn) {
    return (
      <div className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-3 rounded-lg border p-4 text-sm">
        <AlertTriangle className="mt-0.5 size-4" />
        <span>{error ?? "Delivery note not found."}</span>
      </div>
    );
  }

  const lines = dn.lines ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-bold">DN {dn.dnNumber}</h1>
          <p className="text-muted-foreground mt-1 text-xs">{dn.id}</p>
        </div>
        <div className="flex items-center gap-2">
          {dn.status && <Badge variant="outline">{String(dn.status)}</Badge>}
          {/* A delivery note may name the order it fulfils - the third leg of the three-way match. */}
          {dn.purchaseOrderId && (
            <Link to={`/purchase-orders/${dn.purchaseOrderId}`} className="text-sm hover:underline">
              Purchase order
            </Link>
          )}
          {dn.organisationId && (
            <Link
              to={`/organisations/${dn.organisationId}#delivery-notes`}
              className="text-muted-foreground text-sm hover:underline"
            >
              Organisation
            </Link>
          )}
        </div>
      </div>

      <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-4 text-sm md:grid-cols-4">
        <Field label="External ref" value={dn.externalRef ?? null} mono />
        <Field
          label="Supplier"
          value={
            dn.supplierId ? (
              <Link to={`/suppliers/${dn.supplierId}`} className="font-mono text-xs hover:underline">
                {dn.supplierId}
              </Link>
            ) : null
          }
        />
        <Field label="Received" value={dn.receivedAt ? formatDate(dn.receivedAt) : null} />
        <Field label="Lines" value={String(lines.length)} />
        <Field label="Created" value={dn.createdAt ? formatDateTime(dn.createdAt) : null} />
        <Field label="Last modified" value={dn.updatedAt ? formatDateTime(dn.updatedAt) : null} />
      </dl>

      <div className="border-border rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              {/* The order the integrator sent, never renumbered - staff compare it against the source doc. */}
              <TableHead className="w-10 text-right">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>External ref</TableHead>
              <TableHead className="text-right">Received</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Matched</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground text-center">
                  No lines on this note.
                </TableCell>
              </TableRow>
            ) : (
              lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-muted-foreground text-right tabular-nums">
                    {l.sortOrder ?? "-"}
                  </TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell className="font-mono text-xs">{l.sku ?? "-"}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {l.externalRef ?? "-"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(l.quantityReceived)}</TableCell>
                  {/* Structured unitOfMeasure wins over free text: catch-weight matching keys on it. */}
                  <TableCell className="text-muted-foreground">
                    {formatUnit(l.unit, l.unitOfMeasure) ?? "-"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <MatchedQuantity matched={l.matchedQuantity} fullyMatched={l.fullyMatched} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Null matchedQuantity means "not computed", not zero: don't render a 0 that reads as fact.
function MatchedQuantity({
  matched,
  fullyMatched,
}: {
  matched: number | string | null | undefined;
  fullyMatched: boolean | null | undefined;
}) {
  if (matched == null) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {formatNumber(matched)}
      {fullyMatched && <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />}
    </span>
  );
}

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className={`mt-0.5 ${mono ? "font-mono text-xs" : ""}`}>
        {value ?? <span className="text-muted-foreground">-</span>}
      </dd>
    </div>
  );
}
